import { openAuthorizeUrl } from "@/utils/linkedaccounts/linkedAccounts";
import type { SocialQuestData } from "@/utils/linkedaccounts/types";
import {
  Body,
  BulletList,
  Button,
  Heading,
  Icon,
  MenuItem,
  Modal,
  Stack,
} from "@biom3/react";
import type { Content } from "@prismicio/client";
import { PrismicRichText } from "@prismicio/react";
import { useState } from "react";
import { XModalContainer, XModelContentSx } from "./XModalContainer";

type XLikeAndRetweetModalProps = {
  visible: boolean;
  onClose: () => void;
  questData: SocialQuestData;
  questContent: Content.XQuestDataDocument;
};

export function XLikeAndRetweetModal({
  visible,
  onClose,
  questData,
  questContent,
}: XLikeAndRetweetModalProps) {
  const [waitingForCompletion, setWaitingForCompletion] = useState(false);

  const handleRetweet = () => {
    localStorage.removeItem(questData.ruleId);
    openAuthorizeUrl(
      questData.ruleId,
      questData.key,
      questData.questLink,
      questData.gameName,
      questData.gameId,
      false,
    );
    setWaitingForCompletion(true);
  };

  return (
    <Modal
      visible={visible}
      onCloseModal={onClose}
      showBgOverlay={false}
      outsideClicksClose={false}
    >
      <Modal.Content sx={XModelContentSx}>
        <XModalContainer
          waitingForCompletion={waitingForCompletion}
          onClose={onClose}
          ruleId={questData.ruleId}
        >
          <LikeAndRetweetContent
            questContent={questContent}
            onConfirm={handleRetweet}
          />
        </XModalContainer>
      </Modal.Content>
    </Modal>
  );
}

function LikeAndRetweetContent({
  questContent,
  onConfirm,
}: {
  questContent: Content.XQuestDataDocument;
  onConfirm: () => void;
}) {
  return (
    <Stack
      sx={{
        px: "base.spacing.x4",
        height: "100%",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Stack sx={{ width: "85%", alignItems: "left", gap: "base.spacing.x0" }}>
        <MenuItem size="small">
          {questContent.data.retweet_account_profile_picture?.url && (
            <MenuItem.FramedImage
              imageUrl={questContent.data.retweet_account_profile_picture.url}
              circularFrame={true}
            />
          )}
          <MenuItem.Label>
            {questContent.data.retweet_account_display_name || "X User"}
            <Icon
              icon="Authenticated"
              variant="bold"
              sx={{
                alignSelf: "center",
                ml: "base.spacing.x1",
              }}
            />
          </MenuItem.Label>
          <MenuItem.Caption>
            {questContent.data.retweet_account_handle
              ? `@${questContent.data.retweet_account_handle}`
              : ""}
          </MenuItem.Caption>
        </MenuItem>
        {questContent.data.retweet_body && (
          <PrismicRichText
            field={questContent.data.retweet_body}
            components={{
              list: (p) => (
                <BulletList
                  sx={{ "& > ul": { gap: "base.spacing.x2" } }}
                  key={p.key}
                >
                  {p.children}
                </BulletList>
              ),
              listItem: ({ key, ...p }) => (
                <BulletList.Item key={key}>{p.children}</BulletList.Item>
              ),
              paragraph: ({ key, ...p }) => (
                <Body
                  key={key}
                  size="small"
                  sx={{
                    color: "base.color.text.body.secondary",
                  }}
                >
                  {p.children}
                </Body>
              ),
              heading1: ({ key, ...p }) => (
                <Heading
                  key={key}
                  size="xSmall"
                  sx={{ mt: "base.spacing.x4", mb: "base.spacing.x2" }}
                >
                  {p.children}
                </Heading>
              ),
            }}
          />
        )}
        <Stack
          direction="row"
          sx={{
            height: "70%",
            gap: "base.spacing.x1",
            ml: "base.spacing.x2",
          }}
        >
          <Body
            size="xSmall"
            sx={{
              textAlign: "left",
              ml: "base.spacing.x2",
              pt: "base.spacing.x2",
            }}
          >
            <Icon
              icon="SupportChat"
              variant="bold"
              sx={{
                width: "11px",
                height: "11px",
                position: "relative",
                bottom: "-1px",
                mr: "base.spacing.x1",
                ml: "base.spacing.x1",
              }}
            />
            {questContent.data.retweet_comment_count ?? 0}
          </Body>
          <Body
            size="xSmall"
            weight="regular"
            sx={{
              textAlign: "left",
              ml: "base.spacing.x2",
              pt: "base.spacing.x2",
            }}
          >
            <Icon
              icon="Exchange"
              sx={{
                width: "11px",
                height: "11px",
                position: "relative",
                bottom: "-1px",
                mr: "base.spacing.x1",
                ml: "base.spacing.x1",
              }}
            />
            {questContent.data.retweet_retweet_count ?? 0}
          </Body>
          <Body
            size="xSmall"
            weight="regular"
            sx={{
              textAlign: "left",
              ml: "base.spacing.x2",
              pt: "base.spacing.x2",
            }}
          >
            <Icon
              icon="Favourite"
              variant="bold"
              sx={{
                width: "11px",
                height: "11px",
                position: "relative",
                bottom: "-1px",
                mr: "base.spacing.x1",
                ml: "base.spacing.x1",
              }}
            />
            {questContent.data.retweet_like_count ?? 0}
          </Body>
        </Stack>
      </Stack>

      <Button
        variant="primary"
        onClick={onConfirm}
        sx={{ width: "100%", my: "base.spacing.x1" }}
        size="large"
      >
        Like and Repost
      </Button>
    </Stack>
  );
}
